/**
 * Pipeline stage colours.
 *
 * Both ramps live in `index.css` as `--stage-*` tokens — dark from canvas 14d,
 * light from 15e/15g. Light is not a tint of dark: the dark ramp is saturated
 * against near-black and washes out on white, so the light artboards restate
 * every hue darker. Keeping them in CSS means a stage keeps its colour through
 * a theme switch with no re-render and no theme plumbing at each call site.
 *
 * The stage set matches the API's `stage` enum on job–candidate links
 * (`GetCandidatesSearch200ItemStage`). Anything unrecognised falls back to the
 * longlist grey rather than throwing a colour away.
 */

/** Pipeline order, least to most advanced. */
export const STAGE_ORDER = [
  "longlist",
  "shortlist",
  "outbound",
  "screening",
  "submitted",
  "interviewing",
  "hired",
  "rejected",
] as const

const STAGE_IDS = new Set<string>(STAGE_ORDER)

/**
 * The dark ramp, duplicated here only as the `var()` fallback — a stage token
 * missing from the stylesheet should still land on something recognisable
 * rather than on nothing.
 */
const FALLBACK: Record<string, string> = {
  longlist: "#8a929c",
  shortlist: "#7cb9f2",
  outbound: "#b49bf0",
  screening: "#e9a15e",
  submitted: "#d9c46a",
  interviewing: "#2cc5b4",
  hired: "#6fd3a5",
  rejected: "#f4615f",
}

/** A CSS colour for a stage, resolved against whichever theme is showing. */
export function stageHex(stageId: string) {
  if (!STAGE_IDS.has(stageId)) return "var(--stage-longlist, #8a929c)"
  return `var(--stage-${stageId}, ${FALLBACK[stageId]})`
}

/**
 * A stage colour at partial strength, for the pill grounds and hairlines the
 * canvas draws behind stage labels.
 *
 * These used to be hex-with-alpha string concatenation (`${hex}59`), which
 * silently produced garbage the moment the colour stopped being a literal hex.
 * `color-mix` takes any CSS colour, tokens included.
 */
export function stageTint(color: string, percent: number) {
  return `color-mix(in srgb, ${color} ${percent}%, transparent)`
}

/** The three strengths the canvas uses, named so call sites stop guessing. */
export const TINT = { pill: 15, hairline: 35, ground: 8 } as const

/**
 * The canvas's tinted-chip trio: the hue as text, a hairline border of it, and
 * a faint ground of it.
 *
 * Every tinted chip in the app is this same three-line style object, and it had
 * been written out by hand in half a dozen components — each free to drift a
 * tint strength or drop a line without anything noticing. `TonePill` is the
 * component for the ones that are pills; this is for the ones that are not,
 * where the geometry belongs to the row of buttons the chip sits in and only
 * the colour is shared.
 */
export function toneStyle(hex: string) {
  return {
    color: hex,
    borderColor: stageTint(hex, TINT.hairline),
    backgroundColor: stageTint(hex, TINT.ground),
  }
}

/**
 * The avatar tint for a record with no photo, from artboard 5a's six.
 *
 * A token rather than a literal, for the reason the stages are: the artboard
 * ramp is drawn against near-black, and the same six used as the colour of the
 * initials on a white row measure 1.7 to 2.2 against it. Light restates all six
 * darker, and the theme switch costs no render.
 */
const AVATAR_FALLBACK = [
  "#2f6d9e",
  "#96661b",
  "#b1483f",
  "#5560c4",
  "#7b4fae",
  "#2c7d5c",
] as const

export function avatarHex(seed: string) {
  let hash = 0
  for (let index = 0; index < seed.length; index += 1) {
    hash = (hash * 31 + seed.charCodeAt(index)) | 0
  }
  const slot = (Math.abs(hash) % AVATAR_FALLBACK.length) + 1
  return `var(--avatar-${slot}, ${AVATAR_FALLBACK[slot - 1]})`
}

/**
 * The ramp by position rather than by hash, for a chart — where rank is the
 * meaning and two neighbouring series must not collide.
 *
 * The largest share takes the brand itself, which is what 7c draws: teal first,
 * then the avatar ramp. Tokens rather than literals for the usual reason — the
 * artboard's hues are picked against near-black, and light restates them.
 */
export function seriesHex(index: number) {
  if (index <= 0) return "var(--primary)"
  const slot = ((index - 1) % AVATAR_FALLBACK.length) + 1
  return `var(--avatar-${slot}, ${AVATAR_FALLBACK[slot - 1]})`
}
